import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { dynamicDataAttribute, toOneString, formatPrice } from 'helpers/utils'
import Mixin from 'components/common/Mixin'
import Link from 'components/common/Link/Link'
import Text, { H2 } from 'components/common/Text/Text'
import Media from 'components/common/Media'
import { BazaarVoiceRating } from 'components/common/Rating'
import Theme from 'components/common/Theme/Theme'
import Button from 'components/common/Button/Button'
import PhoneLink from 'components/common/PhoneLink'
import { CALL_STORE_FOR_PRICE_FIRST_TEXT } from 'constant'
import { commaCheckAndFormatPrice } from '../../helpers'

const TireLineTile = ({
  handleStoreSearch,
  hasActiveVehicle,
  brandName,
  ImagePath,
  productDisplayName,
  productLink,
  reviewsLink,
  rates,
  season,
  startingListPrice,
  tireType = [],
  handleTireLineClick,
  isSeePriceIncart,
  isShowSelectStore,
  isShowPrice,
  isShowCallStoreForPrice,
  formattedStorePhoneNumber,
  storePhoneNumber,
  seePriceIncartMessage,
  ...props
}) =>
  <Wrapper { ...dynamicDataAttribute('brand-tireline', toOneString(brandName)) }>
    <ImageLink tabIndex="-1" aria-label={ brandName } to={ productLink } onClick={ handleTireLineClick }>
      <Media.Desktop>
        <Image alt={ brandName } data-at-tire-line-tile-image src={ `${ImagePath}?wid=200&hei=200` } />
      </Media.Desktop>
      <Media.Mobile>
        <Image alt={ brandName } data-at-tire-line-tile-image src={ `${ImagePath}?$BOT-TireResultByBrand-Tire-Mobile-w136$` } />
      </Media.Mobile>
    </ImageLink>
    <Information>
      <TireLineTitleText noMargin>
        <Title
          data-at-tire-line-tile-title
          block
          bolder
          size="18"
          to={ productLink }
          onClick={ handleTireLineClick }>
          { productDisplayName }
        </Title>
      </TireLineTitleText>
      <Features data-at-features>
        {
          tireType.map(typeName =>
            <div key={ typeName }>{ typeName }</div>
          )
        }
        <div>{ season }</div>
      </Features>
      <React.Fragment>
        {
          isShowSelectStore &&
            <Price block bold>
              <SelectStoreButton
                data-at-select-store-button
                link
                onClick={ handleStoreSearch }>
                Select store to see the price
              </SelectStoreButton>
            </Price>
        }
        {
          isSeePriceIncart &&
            <PhoneBlock data-at-brand-details-map-price>
              <Phone>{ seePriceIncartMessage }</Phone>
            </PhoneBlock>
        }
        {
          isShowPrice &&
            <Price block bold>
              <Each>Starting at </Each>{ formatPrice(commaCheckAndFormatPrice(startingListPrice)) }<Each> /each</Each>
            </Price>
        }
        {
          isShowCallStoreForPrice &&
            <PhoneBlock data-at-brand-details-product-price>
              <Phone>{ CALL_STORE_FOR_PRICE_FIRST_TEXT }</Phone>
              <Media.Desktop><Phone>{ formattedStorePhoneNumber }</Phone></Media.Desktop>
              <Media.Mobile><StyledPhoneLink number={ storePhoneNumber } /></Media.Mobile>
            </PhoneBlock>
        }
      </React.Fragment>
      <CustomRating { ...props } reviewsLink={ reviewsLink } small />
    </Information>
  </Wrapper>

export default TireLineTile

TireLineTile.propTypes = {
  brandName: PropTypes.string,
  formattedStorePhoneNumber: PropTypes.string,
  handleStoreSearch: PropTypes.func,
  handleTireLineClick: PropTypes.func,
  hasActiveVehicle: PropTypes.bool,
  hasStore: PropTypes.bool,
  ImagePath: PropTypes.string,
  isSeePriceIncart: PropTypes.bool,
  isShowCallStoreForPrice: PropTypes.bool,
  isShowPrice: PropTypes.bool,
  isShowSelectStore: PropTypes.bool,
  productDisplayName: PropTypes.string,
  productLink: PropTypes.string,
  rates: PropTypes.string,
  reviewsLink: PropTypes.string,
  season: PropTypes.string,
  seePriceIncartMessage: PropTypes.string,
  startingListPrice: PropTypes.string,
  storePhoneNumber: PropTypes.string,
  tireType: PropTypes.arrayOf(PropTypes.string)
}

const ImageLink = styled(Link)`
  display: table-cell;
  text-align: center;
  ${Media.desktop`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: block;
    max-height: 228px;
  `}
`

const Image = styled.img`
  max-height: 136px;
  ${Media.desktop`
    max-height: 228px;
  `}
`

const Information = styled.div`
  display: table-cell;
  vertical-align: top;
  ${Media.mobile`
    padding-left: 17px;
  `}
`

const Title = styled(Link)`
  font-size: 18px;
  ${Media.desktop`
    position: absolute;
    top: 228px;
    left: 0;
    right: 0;
    margin-top: 17px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`

const Wrapper = styled.div`
  ${Media.mobile`
    &:not(:first-child) {
      margin-top: 45px;
    }
  `}

  ${Media.desktop`
    position: relative;
    display: table-cell;
    width: 235px;
    padding-top: 265px;
    box-sizing: content-box;
    vertical-align: bottom;

    &:not(:first-child) {
      padding-left: 24px;

      ${ImageLink},
      ${Title} {
        left: 24px;
      }
    }
  `}
`

const Features = styled.div`
  margin-top: 14px;
  line-height: 22px;
`

const Price = styled(Text)`
  margin-top: 6px;
  font-size: 18px;
  line-height: 22px;
`

const Each = styled(Text)`
  color: ${Theme.colors.caption_grey};
`

const CustomRating = styled(BazaarVoiceRating)`
  margin-top: 7px;
`

const PhoneBlock = styled.span`
  display: inline-block;
  margin: 0 6px 9px 0;
  color: ${Theme.colors.black};
  vertical-align: middle;
  ${Mixin.font.GothamBold(14, 600, 19)}
`

const Phone = styled.div`
  font-weight: 700;
`

const StyledPhoneLink = styled(PhoneLink)`
  display: block;
  font-weight: 700;
`

const SelectStoreButton = styled(Button)`
  && {
    padding: 0;
  }
`

const TireLineTitleText = styled(H2)`
  font-size: 26px;
`
